import "./InspectorModal.scss";

import { List } from "immutable";
import _, { isNil } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import InspectorImageWell from "../../components/inspector/InspectorImageWell";
import Identifier from "../../components/interface/Identifier";
import ModalBody from "../../components/modal/ModalBody";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import NormalModalHeading from "../../components/modal/ModalHeading.desktop";
import MobileModalHeading from "../../components/modal/ModalHeading.ios";
import ModalRow from "../../components/modal/ModalRow";
import ModalView from "../../components/modal/ModalView";
import { withScreenContext } from "../../components/screen/ScreenContext";
import compactDisplays from "../../constants/compactDisplay";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import pubSubTopics from "../../constants/pubSubTopics";
import screenModes from "../../constants/screenModes";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { attachmentSelectors } from "../../modules/attachments";
import batchOperations from "../../modules/batchOperations";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { isTimespanZero } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { referenceSelectors } from "../../modules/definition/references";
import { typeSelectors } from "../../modules/definition/types";
import { displayCacheSelectors } from "../../modules/display";
import utils from "../../modules/items/utils";
import { tagSelectors } from "../../modules/tags";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import openUrl from "../../utils/openUrl";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { updateSelectedItemsUsingUpdaters } from "../../utils/selection";
import selectors from "./selectors";

const isIos = preprocessorQuery.isIos();
const isHarvard = preprocessorQuery.isHarvard();
const isMobile = preprocessorQuery.isMobile();

const getReferenceList = (reference, itemData, itemsById, typesById) => {
  const relationships = itemData.relationshipsByRefType.has(reference.id)
    ? itemData.relationshipsByRefType
      .get(reference.id)
      .groupBy(r => r.id + r.subject + r.object)
      .map(r => r.first())
      .toList()
    : List([]);

  const values = relationships
    .map(v => {
      const refItem = itemsById.get(v.object);
      const type = typesById.get(refItem.type);

      return {
        itemId: refItem.id,
        label: getLabelWithUntitled(v.objectLabel),
        subLabel: v.objectAge,
        value: v.object,
        displayId: refItem.displayId,
        color: refItem.color,
        compactDisplay: type.compactDisplay,
        initials: refItem.initials
      };
    })
    .toArray();

  return values.length
    ? {
      id: reference.id,
      label: reference.label,
      values: values
    }
    : null;
};

const getInverseReferenceList = (reference, itemData, itemsById, typesById) => {
  const relationships = itemData.inverseRelationshipsByRefType.has(reference.id)
    ? itemData.inverseRelationshipsByRefType.get(reference.id)
    : List([]);
  const values = relationships
    .map(v => {
      const refItem = itemsById.get(v.object);
      const type = typesById.get(refItem.type);
      return {
        label: getLabelWithUntitled(v.objectLabel),
        subLabel: v.objectAge,
        value: v.object,
        displayId: refItem.displayId,
        color: refItem.color,
        hasAvatar: type.hasAvatar,
        initials: refItem.initials
      };
    })
    .toArray();

  return values.length
    ? {
      id: reference.id,
      label: reference.label,
      values: values
    }
    : null;
};

const getIdentifier = (compactDisplay, itemData, onIdentifierClick) => {
  const isAvatar = compactDisplay === compactDisplays.AVATAR_INITIALS;
  return compactDisplay === compactDisplays.SHORT_LABEL ? null : (
    <Identifier
      isAvatar={isAvatar}
      label={isAvatar ? itemData.initials : itemData.displayId}
      color={itemData.swatchColor || itemData.color}
      onClick={onIdentifierClick}
    />
  );
};

const getPropertyValue = (property, itemData) => {
  let value = itemData.propertyValues.get(property.id);
  if (property.format === PropertyFormatIdentifier.list) {
    const foundOpt = property.allowed ? property.allowed.find(o => o.id === value) : null;
    value = foundOpt ? foundOpt.label : value;
  } else if (property.format === PropertyFormatIdentifier.percentage) {
    value = isNil(value) ? value : `${value}%`;
  }
  return value;
};

class InspectorModal extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this.state = {};

    this.onIdentifierClick = this.onIdentifierClick.bind(this);
    this.renderItemFields = this.renderItemFields.bind(this);
    this.renderDateFields = this.renderDateFields.bind(this);
    this.renderReferenceFields = this.renderReferenceFields.bind(this);
    this.renderNotesFields = this.renderNotesFields.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
  }

  componentDidMount() {
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(pubSubTopics.escapeKeyDown, this.props.onCloseClick);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  onIdentifierClick = id => {
    if (id) {
      PubSub.publish(pubSubTopics.scrollToItem, { id: id });

      updateSelectedItemsUsingUpdaters(
        false,
        id,
        this.props.selectedItemIds,
        this.props.onRemoveFromSelection,
        this.props.onAddToSelection,
        this.props.onSelectItems
      );
    } else {
      PubSub.publish(pubSubTopics.scrollToItem, { id: this.props.itemData.itemId });
    }
  };

  onLinkClick = id => {
    const path = this.props.attachments.get(id).path;
    openUrl(path);
  };

  onAttachmentClick = attachmentId => {
    PubSub.publish(pubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.OPEN_ATTACHMENT,
      attachmentId
    });
  };

  onImageClick = attachmentId => {
    PubSub.publish(pubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.OPEN_ATTACHMENT,
      attachmentId
    });
  };

  renderItemFields = () => {
    const { itemData, items, itemIds, typesById, activeViewId } = this.props;
    const superItem = itemData.super ? items.find(item => item.id === itemData.super) : null;
    const superLabel = superItem ? getLabelWithUntitled(superItem.label) : null;

    let typeChildren = [
      <ModalField key="type" flex={1} label="Type">
        <Fragment>{typesById.get(itemData.type).label}</Fragment>
      </ModalField>
    ];
    if (superLabel) {
      typeChildren.push(
        <ModalField key="super" flex={1} label="Parent">
          <Fragment>{superLabel}</Fragment>
        </ModalField>
      );
    }

    if ((isHarvard || isMobile) && itemData.hasChildren) {
      typeChildren.push(
        <ModalField key="children" flex={1} showBlankLabel={true}>
          <FormCheckbox
            label="Expand Children"
            checkboxState={!itemIds.find(itemId => !this.isExpanded(itemId))}
            onChange={newState => this.onToggleChildren(activeViewId, itemData.id, newState)}
          />
        </ModalField>
      );
    }

    return (
      <ModalGroup>
        <ModalRow>{typeChildren}</ModalRow>
      </ModalGroup>
    );
  };

  renderDateFields = () => {
    const { itemData } = this.props;
    let dateChildren = [];
    if (isDateValid(itemData.preferredStartDate)) {
      dateChildren.push(
        <ModalField key="startDate" label={`${isDateValid(itemData.latestStartDate) ? "Earliest " : ""}Start Date`}>
          <span className={utils.arePreferredStartDifferentFromActualStart(itemData) ? "isWarning" : ""}>
            {itemData.preferredStartDate.label}
          </span>
        </ModalField>
      );
    }
    if (isDateValid(itemData.latestStartDate)) {
      dateChildren.push(
        <ModalField key="latestStartDate" label="Latest Start Date">
          <Fragment>{itemData.latestStartDate.label}</Fragment>
        </ModalField>
      );
    }
    if (isDateValid(itemData.earliestEndDate)) {
      dateChildren.push(
        <ModalField key="earliestEndDate" label="Earliest End Date">
          <Fragment>{itemData.earliestEndDate.label}</Fragment>
        </ModalField>
      );
    }
    if (isDateValid(itemData.preferredEndDate) && itemData.preferredStartDate.timestamp != itemData.endDate.timestamp) {
      dateChildren.push(
        <ModalField key="endDate" label={`${isDateValid(itemData.earliestEndDate) ? "Latest " : ""}End Date`}>
          <span className={utils.arePreferredEndDifferentFromActualEnd(itemData) ? "isWarning" : ""}>
            {itemData.preferredEndDate.label}
          </span>
        </ModalField>
      );
    }
    if (!isTimespanZero(itemData.preferredDuration)) {
      dateChildren.push(
        <ModalField key="duration" label={"Duration"}>
          <span className={utils.arePreferredDatesDifferentFromActualDates(itemData) ? "isWarning" : ""}>
            {itemData.preferredDuration.label}
          </span>
        </ModalField>
      );
    }

    return dateChildren.length ? (
      <ModalGroup label="Dates" icon="inspectorTime" iconColor="iconMono">
        <ModalRow>{dateChildren}</ModalRow>
      </ModalGroup>
    ) : null;
  };

  getOnClickReferenceItem = itemId => () => this.props.onSelectItems(itemId);

  renderReferenceFields = () => {
    const { itemData, typeData, itemsById, typesById } = this.props;
    const emptyReferences = typeData.references.size === 0 && typeData.inverseReferences.size === 0;
    const references = typeData.references
      .map(ref => getReferenceList(ref, itemData, itemsById, typesById))
      .filter(ref => ref);
    const inverseReferences = typeData.inverseReferences
      .map(ref => getInverseReferenceList(ref, itemData, itemsById, typesById))
      .filter(ref => ref);

    let referenceChildren = [];
    references.map(ref => {
      if (ref.values.length) {
        referenceChildren.push(
          <ModalField flex={1} label={ref.label} key={ref.id}>
            <Fragment>
              {ref.values.map(value => (
                <div
                  key={value.value}
                  onClick={this.getOnClickReferenceItem(value.itemId)}
                  className={`reference ${value.hasAvatar ? "hasAvatar" : ""}`}
                >
                  {getIdentifier(value.compactDisplay, value)}
                  {value.label}
                </div>
              ))}
            </Fragment>
          </ModalField>
        );
      }
    });
    inverseReferences.map(ref => {
      if (ref.values.length) {
        referenceChildren.push(
          <ModalField flex={1} label={ref.label} key={ref.id}>
            <Fragment>
              {ref.values.map(value => (
                <div
                  key={value.value}
                  className={`reference ${value.hasAvatar ? "hasAvatar" : ""}`}
                  onClick={this.getOnClickReferenceItem(value.itemId)}
                >
                  {getIdentifier(value.compactDisplay, value)}
                  {value.label}
                </div>
              ))}
            </Fragment>
          </ModalField>
        );
      }
    });

    return !emptyReferences && referenceChildren.length ? (
      <ModalGroup label="Relationships" icon="inspectorRelationships" iconColor="iconMono">
        <div className="inspectorModalRelationships">
          <ModalRow>{referenceChildren}</ModalRow>
        </div>
      </ModalGroup>
    ) : null;
  };

  renderNotesFields = () => {
    const { itemData, typeData, attachments, globalTags } = this.props;
    const tags = itemData.tags.map(tId => globalTags.get(tId)).toArray();
    const properties = typeData.properties.filter(property => itemData.propertyValues.get(property.id));
    const selectedImage = !_.isNil(this.props.itemData.image) ? attachments.get(this.props.itemData.image) : null;
    const selectedLinks = itemData.links.map(id => {
      return {
        id,
        label: attachments.get(id).label,
        url: attachments.get(id).path
      };
    });
    const selectedAttachments = itemData.attachments.map(id => {
      const attachment = attachments.get(id);
      return {
        id,
        label: attachment.label ? attachment.label : null
      };
    });

    let notesChildren = [];
    if (itemData.summary) {
      notesChildren.push(
        <ModalField key="summary" flex={1} label="Summary">
          <Fragment>{itemData.summary}</Fragment>
        </ModalField>
      );
    }
    if (tags.length) {
      notesChildren.push(
        <ModalField key="tags" flex={1} label="Tags">
          <Fragment>{tags.join(", ")}</Fragment>
        </ModalField>
      );
    }
    properties.map(property => {
      const value = getPropertyValue(property, itemData);
      if (value) {
        notesChildren.push(
          <ModalField flex={1} label={property.label} key={property.id}>
            <Fragment>{value}</Fragment>
          </ModalField>
        );
      }
    });
    if (selectedImage) {
      notesChildren.push(
        <ModalField key="image" flex={1} label="Image">
          <InspectorImageWell source={selectedImage.path} onView={() => this.onImageClick(this.props.itemData.image)} />
        </ModalField>
      );
    }
    if (selectedLinks.size) {
      notesChildren.push(
        <ModalField key="links" flex={1} label="Links">
          <Fragment>
            {selectedLinks.map(attachment => {
              const title = attachment.label;
              const url = attachment.url;
              const id = attachment.id;
              const onClick = this.onLinkClick;
              const titleEl = title ? (
                url ? (
                  <div>{title}</div>
                ) : (
                  <FormButton linkStyle={true} label={title} onClick={() => onClick(id)} />
                )
              ) : null;
              const urlEl = url ? <FormButton linkStyle={true} label={url} onClick={() => onClick(id)} /> : null;

              return (
                <div key={`list-item-${id}`}>
                  {titleEl}
                  {urlEl}
                </div>
              );
            })}
          </Fragment>
        </ModalField>
      );
    }
    if (selectedAttachments.size) {
      notesChildren.push(
        <ModalField key="attachments" flex={1} label="Attachments">
          <Fragment>
            {selectedAttachments.map(attachment => {
              const title = attachment.label;
              const url = attachment.url;
              const id = attachment.id;
              const onClick = this.onAttachmentClick;
              const titleEl = title ? (
                url ? (
                  <span>{title}</span>
                ) : (
                  <FormButton linkStyle={true} label={title} onClick={() => onClick(id)} />
                )
              ) : null;
              const urlEl = url ? <FormButton linkStyle={true} label={url} onClick={() => onClick(id)} /> : null;

              return (
                <div key={`list-item-${id}`}>
                  {titleEl}
                  {urlEl}
                </div>
              );
            })}
          </Fragment>
        </ModalField>
      );
    }
    if (itemData.locationUri) {
      notesChildren.push(
        <ModalField key="locationUri" flex={1} label="Location URI">
          <ModalRow>{itemData.locationUri}</ModalRow>
        </ModalField>
      );
    }

    return notesChildren.length ? (
      <ModalGroup className="notesGroup" label="Notes" icon="inspectorProperties" iconColor="iconMono">
        <ModalRow>{notesChildren}</ModalRow>
      </ModalGroup>
    ) : null;
  };

  onToggleChildren(activeViewId, itemIid, value) {
    if (
      (isHarvard || isMobile) &&
      this.props.screenContext &&
      this.props.screenContext.screenMode === screenModes.MobilePortrait
    ) {
      this.props.toggleExpandSuperItemInMobileList(activeViewId, itemIid, value);
    } else {
      this.props.toggleExpandSuperItem(activeViewId, itemIid, value);
    }
  }

  isExpanded(itemId) {
    const items =
      (isHarvard || isMobile) &&
      this.props.screenContext &&
      this.props.screenContext.screenMode === screenModes.MobilePortrait
        ? this.props.expandedItemsForMobile
        : this.props.expandedItems;
    return items.includes(itemId);
  }

  onBackdropClick() {
    if (isIos) {
      this.props.onCloseClick();
    }
  }

  getMobileModalHeading() {
    const { onCloseClick } = this.props;
    return isMobile ? (
      <MobileModalHeading
        title="Item Details"
        showCancel={!isMobile}
        showBack={isMobile}
        onCancelClick={onCloseClick}
        onBackClick={onCloseClick}
        showDone={!isIos}
      />
    ) : null;
  }

  getNormalModalHeading() {
    const { itemData, typeData, onCloseClick } = this.props;
    const itemLabel = getLabelWithUntitled(itemData.label);

    return (
      <NormalModalHeading
        title={itemLabel}
        icon={typeData.icon ? typeData.icon : null}
        iconColor={typeData.color ? typeData.color : null}
        showCancel={!isMobile}
        showBack={isMobile}
        onCancelClick={onCloseClick}
        onBackClick={onCloseClick}
        showDone={!isIos}
      />
    );
  }

  getEmptyModalView(mobileModalHeading, normalModalHeading) {
    const header = (
      <Fragment>
        {mobileModalHeading}
        {normalModalHeading}
      </Fragment>
    );

    return (
      <div className="inspectorModal">
        <ModalView
          className="inspectorModal"
          header={header}
          footer={null}
          fixedHeight={null}
          onBackdropClick={this.onBackdropClick}
        >
          <ModalBody>No items selected.</ModalBody>
        </ModalView>
      </div>
    );
  }

  getNormalModalView(mobileModalHeading, normalModalHeading) {
    const header = (
      <Fragment>
        {mobileModalHeading}
        {isMobile ? null : normalModalHeading}
      </Fragment>
    );

    const { itemData, typeData, calendar } = this.props;
    const identifier = getIdentifier(typeData.compactDisplay, itemData, this.onIdentifierClick);
    const { startStr, endStr, durationStr } = utils.getActualDateStrings(calendar, itemData);

    const subHeader = (
      <div className="inspectorModalHeading">
        <ModalGroup>
          <div>
            {isDateValid(itemData.preferredStartDate) ? (
              <ModalRow>
                <Fragment>
                  {startStr}
                  {startStr !== endStr ? ` - ${endStr} (${durationStr})` : ""}
                </Fragment>
              </ModalRow>
            ) : null}
            <ModalRow>
              <Fragment>{identifier}</Fragment>
            </ModalRow>
          </div>
        </ModalGroup>
      </div>
    );

    return (
      <div className="inspectorModal">
        <ModalView
          className="inspectorModal"
          header={header}
          footer={null}
          fixedHeight={null}
          onBackdropClick={this.onBackdropClick}
        >
          <ModalBody>
            {isMobile ? null : subHeader}
            <div className="scrollableContainer">
              {isMobile ? normalModalHeading : null}
              {isMobile ? subHeader : null}
              {this.renderItemFields()}
              {this.renderDateFields()}
              {this.renderReferenceFields()}
              {this.renderNotesFields()}
            </div>
          </ModalBody>
        </ModalView>
      </div>
    );
  }

  render() {
    const { itemIds } = this.props;

    const mobileModalHeading = this.getMobileModalHeading();

    const modalHeading = this.getNormalModalHeading();

    return itemIds.size === 0
      ? this.getEmptyModalView(mobileModalHeading, modalHeading)
      : this.getNormalModalView(mobileModalHeading, modalHeading);
  }
}

InspectorModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  itemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  itemData: PropTypeShapes.inspectorItem,
  items: PropTypeShapes.inspectorItemList,
  typeData: PropTypeShapes.denormalisedTypeDefinition,
  calendar: PropTypeShapes.calendar.isRequired,
  attachments: ImmutablePropTypes.map,
  itemsById: ImmutablePropTypes.map.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  sortedItemTypes: ImmutablePropTypes.list,
  referencesById: ImmutablePropTypes.map.isRequired,
  toggleExpandSuperItem: PropTypes.func,
  toggleExpandSuperItemInMobileList: PropTypes.func,
  expandedItems: ImmutablePropTypes.list,
  expandedItemsForMobile: ImmutablePropTypes.list,
  activeViewId: PropTypes.string,
  globalTags: ImmutablePropTypes.map.isRequired,
  //#!if harvard === true || mobile === true
  // From withScreenContext
  screenContext: PropTypes.object
  //#!endif
};

const mapStateToProps = state => {
  const inspectorData = selectors.getInspectorData(state.present);
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  return {
    itemIds: inspectorData.itemIds,
    items: inspectorData.items,
    itemData: inspectorData.itemData,
    typeData: inspectorData.typeData,
    calendar: calendarSelectors.getCalendar(state.present),
    attachments: attachmentSelectors.getAttachmentsById(state.present),
    itemsById: displayCacheSelectors.getDisplayItemsById(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    sortedItemTypes: selectors.getSortedItemTypes(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    expandedItems: appStateSelectors.getViewExpandedItems(state.present, activeViewId),
    activeViewId: activeViewId,
    globalTags: tagSelectors.getTags(state.present),
    //#!if harvard === true || mobile === true
    expandedItemsForMobile: appStateSelectors.getViewExpandedItemsForMobile(state.present)
    //#!endif
  };
};

const editingActions = {};

const readonlyActions = {
  onCloseClick: isIos ? batchOperations.hideInspectorModalAndClearSelection : uiStateOperations.hideInspectorModal,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onSelectItems: appStateOperations.selectItems,
  toggleExpandSuperItem: appStateOperations.toggleExpandSuperItem,
  //#!if harvard === true || mobile === true
  toggleExpandSuperItemInMobileList: appStateOperations.toggleExpandSuperItemInMobileList
  //#!endif
};

let exportedView = InspectorModal;
//#!if harvard === true || mobile === true
exportedView = withScreenContext(InspectorModal);
//#!endif

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(exportedView);
